// frontend/src/utils/kpiValueCoverage.js
//
// Which explanation a money KPI tile owes the merchant, decided once.
//
// Four tiles sit in one strip on Home — Inventory value, Projected excess,
// Stockout risk (and Sales, which needs no explanation) — and three of them are
// sums of (quantity × a unit price the merchant may not have entered yet).
// Every tile has to answer the same question before showing a number: is this
// value complete, and if not, why? Spelling that cascade per tile is how one
// payload ends up telling one merchant several different stories — and how one
// tile ends up honest about its coverage while the tile beside it stays quiet.
//
// These return a state, not an i18n key: the WORDING is the caller's, mapped
// into its own namespace.
//
// TWO dimensions, never merged. Inventory value and projected excess are short
// when an item has no COST (Data Health `missing_cost`); stockout risk is short
// when it has no SELL price (`missing_sell_price`). Same cascade shape, two
// different gaps with two different remedies, so each entry point names the one
// it reports and no state ever carries the other's word. The backend counts
// them apart for the same reason (core/services/cost_valuation.disclose_gap).
//
// Each KPI also differs in its PRECONDITIONS — all three need a synchronized
// catalogue, excess and stockout risk additionally need forecasts — so each
// gets its own entry point asking the ones it actually has (each is worded
// differently), over one shared coverage cascade and one shared
// synchronization check.
//
// A short value is a fact; whether to SAY so is the merchant's. Each payload
// carries the backend's answer (`partial_cost` / `partial_sell_price`, and
// `change_partial` for the change chip, all from
// core.services.cost_valuation.disclose_gap), false once the merchant muted
// that gap's Data Health issue, so a muted gap reads as `full`. A value nothing
// could price stays `noCost` / `noSellPrice` either way — the dash on the tile
// needs its reason.
//
// No forecast has three readings, told apart by the forecast run the order
// import started (`utils/syncGate.forecastRunState`): it is still being
// calculated, it failed, or there is no run to wait on and the store has no
// forecast row at all. Short sales history is never the reason: the engine
// forecasts zero for an item without enough of it
// (forecast_engine/service.py, `insufficient_data`).

import { FORECAST_RUN } from './syncGate';

// The states that leave a tile with no number to show.
const NO_VALUE_STATES = new Set([
  'notSynchronized', 'noForecast', 'forecastCalculating', 'forecastFailed',
  'noCost', 'noSellPrice',
]);

/**
 * Is there a number on the tile at all? Asked by the value AND by the change
 * chip: `KpiValue` renders `changeText` off `change` alone, so a change would
 * print beside the no-value dash unless both read this one answer.
 *
 * @param {{state: string}|null} coverage  a state from this module, or null
 *   while the KPI has not loaded
 */
export function kpiHasValue(coverage) {
  return coverage != null && !NO_VALUE_STATES.has(coverage.state);
}

/**
 * Why a Home view that needs forecasts has none: the two forecast KPI tiles
 * and the Stockout risk grid (`components/charts/ShortageTable`).
 *
 * @param {string|null} forecastRun  `utils/syncGate.forecastRunState`
 */
export function noForecast(forecastRun) {
  if (forecastRun === FORECAST_RUN.CALCULATING) return { state: 'forecastCalculating', count: 0 };
  if (forecastRun === FORECAST_RUN.FAILED) return { state: 'forecastFailed', count: 0 };
  return { state: 'noForecast', count: 0 };
}

/**
 * The shared tail, reached once a KPI's own precondition holds.
 *
 * @param {*}       value            the headline; tested for presence only,
 *                                   so a string or a number is equally fine
 * @param {number}  missingItems     items holding the thing being valued that
 *                                   have no price in this KPI's dimension
 * @param {boolean} discloseGap      the payload's `partial_cost` /
 *                                   `partial_sell_price`: false when the
 *                                   merchant muted this gap
 * @param {boolean} changePartial    the payload's `change_partial`
 * @param {string}  missingState     what "no price reaches this number at all"
 *                                   is CALLED in this dimension — the one word
 *                                   that differs between cost and sell price,
 *                                   and the reason this tail is shared rather
 *                                   than copied per KPI
 */
function coverageTail(value, missingItems, discloseGap, changePartial, missingState) {
  // Read as "muted", a missing flag would silence a warning nobody muted.
  if (typeof discloseGap !== 'boolean') {
    throw new Error(`KPI payload carries no partial_cost / partial_sell_price flag (got ${discloseGap})`);
  }
  // A null value reaching HERE has one reading: stock was held (or at risk)
  // and not one unit of it could be priced, which makes `missingItems > 0`.
  // Every headline on these tiles goes through one rule, `core.utils.
  // priced_total`, which returns null only for a non-empty population none of
  // which could be priced. The other null — an EMPTY population, which that
  // rule never sees — is caught above by the `itemsWithCost === 0` branch, so
  // it cannot arrive here wearing a zero count.
  if (value == null) return { state: missingState, count: missingItems };

  // A shortfall in the headline outranks one in the badge beside it.
  if (missingItems > 0 && discloseGap) return { state: 'partial', count: missingItems };
  if (changePartial) return { state: 'partialHistory', count: 0 };
  return { state: 'full', count: 0 };
}

/**
 * The cost half, shared by the two cost-valued KPIs.
 *
 * @param {number}  kpi.itemsWithCost    `items_with_cost` — catalogue-wide
 * @param {*}       kpi.value            `latest_value`
 * @param {number}  kpi.uncostedItems    items holding the thing being valued
 *                                       that have no cost price
 * @param {boolean} kpi.discloseGap      `partial_cost`
 * @param {boolean} kpi.changePartial    `change_partial`
 */
function costCoverage({ itemsWithCost, value, uncostedItems, discloseGap, changePartial }) {
  // Two roads to "add cost prices": nothing in the catalogue is costed, or the
  // valued stock specifically isn't and there is consequently no value to
  // show. Same merchant action, same sentence — only the count differs, and
  // this branch has none to give.
  if (itemsWithCost === 0) return { state: 'noCost', count: 0 };
  return coverageTail(value, uncostedItems, discloseGap, changePartial, 'noCost');
}

/**
 * The catalogue is not synchronized yet, so there is nothing to value: none
 * of it has arrived, or the store's first import is still bringing it in
 * (`utils/syncGate.catalogImporting`) and a sum over the stock so far would
 * read as the store's.
 *
 * Shared by the three stock tiles and the Stockout risk grid
 * (`components/charts/ShortageTable`) rather than spelled per tile: it is one
 * fact about the store, every payload answers it with one field (backend
 * `reporting.kpi_comparison.synchronized_item_count`), and one string says it
 * (`home.metrics.notSynchronizedShort`). Asked by one tile only, a store with
 * nothing synchronized would read as a cost or forecast problem on the tile
 * beside it — sending the merchant off to fix a catalogue that has not
 * arrived.
 *
 * Returns null when the precondition holds, so callers chain with `??` and
 * each keeps its own next question.
 */
export function notSynchronized(totalItems, catalogImporting) {
  // Read as synchronized, a missing count would show a number over nothing.
  if (typeof totalItems !== 'number') {
    throw new Error(`KPI payload carries no total_items count (got ${totalItems})`);
  }
  return totalItems === 0 || catalogImporting ? { state: 'notSynchronized', count: 0 } : null;
}

/**
 * @param {object}  kpi
 * @param {number}  kpi.totalItems       `total_items` — distinct items with
 *                                       an inventory record; zero means
 *                                       nothing has synchronized yet
 * @param {boolean} kpi.catalogImporting `utils/syncGate.catalogImporting`
 * @param {boolean} kpi.hasForecasts     `has_forecasts`
 * @param {string|null} kpi.forecastRun  `utils/syncGate.forecastRunState`
 * @param {number}  kpi.itemsWithCost    `items_with_cost`
 * @param {*}       kpi.value            `latest_value`
 * @param {number}  kpi.uncostedItems    `uncosted_excess_items`
 * @param {boolean} kpi.discloseGap      `partial_cost`
 * @param {boolean} kpi.changePartial    `change_partial`
 * @returns {{state: string, count: number}} state is one of
 *   `notSynchronized` — the catalogue has not arrived, or is arriving
 *   `noForecast` — nothing to project excess against, and no forecast run
 *                  to wait on
 *   `forecastCalculating` — no forecast yet; the first one is being
 *                  calculated
 *   `forecastFailed` — no forecast; the run calculating it failed
 *   `noCost`     — no cost price reaches this number at all
 *   `partial`    — the value is short by `count` items
 *   `partialHistory` — the value is sound, the change beside it is not
 *   `full`       — everything priced, or the gap is muted
 *
 * Synchronization is asked BEFORE forecasts: a store with no catalogue has no
 * forecasts either, and what it can act on is the sync, not the sales history
 * it has not started accumulating.
 */
export function excessCostState({
  hasForecasts, forecastRun, totalItems, catalogImporting, ...cost
}) {
  return notSynchronized(totalItems, catalogImporting)
    ?? (hasForecasts === false ? noForecast(forecastRun) : costCoverage(cost));
}

/**
 * @param {object}  kpi
 * @param {number}  kpi.totalItems       `total_items` — distinct items with
 *                                       an inventory record, held or not.
 *                                       Zero means nothing has synchronized
 *                                       yet; it says nothing about coverage.
 * @param {boolean} kpi.catalogImporting `utils/syncGate.catalogImporting`
 * @param {number}  kpi.itemsWithCost    `items_with_cost`
 * @param {*}       kpi.value            `latest_value`
 * @param {number}  kpi.uncostedItems    `uncosted_inventory_items` — items
 *                                       actually HOLDING stock with no cost
 *                                       price. NOT `totalItems -
 *                                       itemsWithCost`: an uncosted item
 *                                       sitting at zero stock costs the value
 *                                       nothing, and sending the merchant to
 *                                       price it is sending them nowhere.
 * @param {boolean} kpi.discloseGap      `partial_cost`
 * @param {boolean} kpi.changePartial    `change_partial`
 * @returns {{state: string, count: number}} same states as
 *   :func:`excessCostState`, except that the precondition is
 *   `notSynchronized` — the inventory has not arrived, or is arriving — rather than
 *   `noForecast`.
 */
export function inventoryCostState({ totalItems, catalogImporting, ...cost }) {
  return notSynchronized(totalItems, catalogImporting) ?? costCoverage(cost);
}

/**
 * The SELL-price twin, for the Stockout risk tile: its value is
 * (shortage units × sell price), so an item the merchant has not priced
 * shortens it exactly as an uncosted item shortens the two beside it — and
 * points at a different Data Health card.
 *
 * There is no `items_with_sell_price` counterpart to `itemsWithCost` here:
 * this KPI's own `unpricedItems` measures the gap where it actually bites.
 *
 * @param {object}  kpi
 * @param {number}  kpi.totalItems       `total_items` — zero means nothing
 *                                       has synchronized yet
 * @param {boolean} kpi.catalogImporting `utils/syncGate.catalogImporting`
 * @param {boolean} kpi.hasForecasts     `has_forecasts` — no forecast, no
 *                                       projected shortage to value
 * @param {string|null} kpi.forecastRun  `utils/syncGate.forecastRunState`
 * @param {*}       kpi.value            `total_risk_value`; null when stock was
 *                                       at risk and none of it had a price
 * @param {number}  kpi.unpricedItems    `unpriced_items`
 * @param {boolean} kpi.discloseGap      `partial_sell_price`
 * @param {boolean} kpi.changePartial    `change_partial`
 * @returns {{state: string, count: number}} same shape as the cost states,
 *   with `noSellPrice` in place of `noCost` — a distinct state because it is
 *   a distinct sentence with a distinct remedy — and the same
 *   `notSynchronized` and three no-forecast states as :func:`excessCostState`,
 *   asked in the same order.
 */
export function stockoutSellPriceState({
  totalItems, catalogImporting, hasForecasts, forecastRun,
  value, unpricedItems, discloseGap, changePartial,
}) {
  return notSynchronized(totalItems, catalogImporting)
    ?? (hasForecasts === false
      ? noForecast(forecastRun)
      : coverageTail(value, unpricedItems, discloseGap, changePartial, 'noSellPrice'));
}
